import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { type Connecting, Connections } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Waiting } from '@beyond-js/ui/react';
import { Heading, Outage, Problem, useArmed, useReauthentication } from '@accounts/ui';
import { Choosing, type Decision } from './choosing';

/** What went wrong with a decision: the refusal when there was one, and the words when the screen knows better */
interface Failure {
	error?: unknown;
	words?: string;
	/** The browser would not open the sign-in window: a press of the person's own opens it, and only confirms */
	blocked?: boolean;
}

/**
 * The person's decision on an application's request (`/connect?ticket=`): the request read, then the
 * choice of what it may do (`Choosing`) and the decision sent.
 *
 * Allow waits `ARMED` after the page is shown and after every return to it (`useArmed`), and a sign-in
 * older than the re-authentication window is confirmed in place first (`useReauthenticate`), as it is
 * when the service answers `REAUTHENTICATION_REQUIRED`. Each decision is sent once (`replay: false`) and
 * the page is replaced by the address the service answered, so Back never decides again. An account that can only
 * sign in again with its provider does so in a window (`useReauthentication`): the ways to prove it are read with a
 * request that asks for it, so Allow opens the window inside the press, and a browser that blocked it anyway is
 * offered a press that opens it. That press, and a proof that arrived after the page stopped waiting, only confirm
 * it is the person: the page says so and Allow then sends the choice in view. A decision is sent after a window
 * only when the choice is still the one Allow was pressed on; when the person changed it meanwhile, the proof is
 * kept and Allow sends what they see now. The choice stays open throughout, and nothing is sent twice. Allow sends
 * what the person chose and the access they reviewed; when that access changed meanwhile
 * (`ACCESS_CHANGED`) nothing was decided, and the page reads the request again and says so. A request no
 * longer valid is said as gone, an outage is the page's `Outage`, and inside another page's frame nothing is offered
 * to decide.
 */
export function Asking({ ticket }: { ticket: string }) {
	const { text, client, language } = usePresence();
	const connections = useMemo(() => new Connections(client), [client]);
	const [notice, setNotice] = useState<string>();
	// Confirmed in place on this page, so Allow does not ask again for it
	const confirmed = useRef(false);
	const { reauthenticate, prime } = useReauthentication({
		late: () => {
			confirmed.current = true;
			setFailure(undefined);
			setNotice(text('connect.again.late'));
		}
	});
	// How many times the choice on screen changed, so a decision made before a window is not sent for another choice
	const edits = useRef(0);
	// A decision or a confirmation under way: a second press starts nothing
	const pending = useRef(false);
	const [asked, setAsked] = useState<Connecting>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<Failure>();
	const [busy, setBusy] = useState<'allow' | 'cancel' | 'confirm'>();
	// The access changed while the page was open, and the request was read again
	const [changed, setChanged] = useState(false);
	const started = useRef(false);

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setUnread(Refusal.kept);
		if (!ticket) {
			setUnread(new Refusal('EXCHANGE_INVALID', 'No request to decide'));
			return true;
		}
		try {
			const answer = await connections.ask(ticket);
			// It will ask: the ways to prove it are read now, so a press can open a window at once
			if (answer.reauthenticate) prime();
			setAsked(answer);
			setUnread(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', String(error));
			setUnread(given);
			return !given.unreachable;
		}
	}, [connections, ticket]);

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		void read();
	}, [read]);

	/** Confirms it is the person when the request asks for it; false when they did not, `blocked` for a blocked window */
	async function proved(): Promise<boolean | 'blocked'> {
		if (confirmed.current) return true;
		const result = await reauthenticate();
		confirmed.current = result === true;
		return result;
	}

	/** Why a decision was not sent: the window was blocked (with a press that opens it), or it was not confirmed */
	function unproved(result: boolean | 'blocked', error?: unknown) {
		pending.current = false;
		setBusy(undefined);
		setFailure({ error, words: text(result === 'blocked' ? 'connect.again.blocked' : 'connect.reauth'), blocked: result === 'blocked' });
	}

	/** Proved while the choice changed, or by the press that opens a blocked window: Allow sends the choice in view */
	function confirmedOnly() {
		pending.current = false;
		setBusy(undefined);
		setFailure(undefined);
		setNotice(text('connect.again.late'));
	}

	/** The press that opens a window the browser blocked: it confirms it is the person, and decides nothing */
	async function confirm() {
		if (pending.current) return;
		pending.current = true;
		setBusy('confirm');
		setFailure(undefined);
		setNotice(undefined);
		const result = await proved();
		if (result === true) confirmedOnly();
		else unproved(result);
	}

	async function decide(decision?: Decision) {
		if (pending.current) return;
		pending.current = true;
		const allowed = Boolean(decision);
		const at = edits.current;
		setBusy(allowed ? 'allow' : 'cancel');
		setFailure(undefined);
		setNotice(undefined);
		const send = () => (decision ? connections.allow(ticket, decision.selected, decision.organization) : connections.cancel(ticket));
		try {
			if (allowed && asked?.reauthenticate) {
				const result = await proved();
				if (result !== true) return unproved(result);
				if (edits.current !== at) return confirmedOnly();
			}
			let destination: string;
			try {
				destination = await send();
			} catch (error) {
				// The window closed since the page read the request: confirmed in place, then sent once more
				if (!(allowed && Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED')) throw error;
				confirmed.current = false;
				const result = await proved();
				if (result !== true) return unproved(result, error);
				if (edits.current !== at) return confirmedOnly();
				destination = await send();
			}
			globalThis.location.replace(destination);
		} catch (error) {
			pending.current = false;
			setBusy(undefined);
			if (Refusal.is(error) && error.quiet) return;
			// A request already decided, expired or of another session is said as gone, with no way to decide it
			if (Refusal.is(error) && error.code === 'EXCHANGE_INVALID') {
				setAsked(undefined);
				setUnread(error);
				return;
			}
			// Nothing was decided: the person chooses again on what the app has now
			if (Refusal.is(error) && error.code === 'ACCESS_CHANGED') {
				setChanged(true);
				await read();
				return;
			}
			setFailure({ error });
		}
	}

	// Inside another page's frame a press could be steered by what that page draws over this one, so the
	// decision is offered only in a window of its own
	const framed = globalThis.top !== globalThis.self;
	const armed = useArmed(Boolean(asked) && !framed);
	const reading = !asked && !unread;
	const gone = unread && !unread.unreachable;

	// The request is the page's one read: Accounts not answering it is the page's body (D163), under the H1 that
	// names the place while the request cannot be read (D158). It stays first in every form of the page, so a
	// Try again that is answered leaves focus on the page's heading
	const outage = (
		<>
			{unread ? <Heading title={text('place.connect')} /> : null}
			<Outage whole reads={[{ key: 'request', title: text('outage.request'), unread, run: read }]} />
		</>
	);
	if (reading) {
		return (
			<>
				{outage}
				<Waiting label={text('connect.reading')} language={language} kind="lines" rows={3} />
			</>
		);
	}
	if (unread?.unreachable) return <>{outage}</>;
	if (gone || !asked) {
		return (
			<>
				{outage}
				<Callout tone="warning" live title={text('connect.gone')} />
				<Link to="/" className="self-start underline">
					{text('connect.onward')}
				</Link>
			</>
		);
	}
	if (framed) {
		return (
			<>
				{outage}
				<Callout tone="warning" live title={text('connect.framed')} />
			</>
		);
	}

	return (
		<>
			{outage}
			<Choosing
				asked={asked}
				busy={busy}
				armed={armed}
				changed={changed}
				note={asked.reauthenticate && !confirmed.current ? <p className="text-small text-ink-muted">{text('connect.reauthenticate')}</p> : null}
				onAllow={decision => void decide(decision)}
				onCancel={() => void decide()}
				onChange={() => void edits.current++}
			/>
			{notice ? <Callout tone="info" live title={notice} /> : null}
			{failure?.blocked ? (
				<Callout
					tone="warning"
					live
					title={failure.words ?? ''}
					actions={
						<Button variant="secondary" busy={busy === 'confirm'} onClick={() => void confirm()}>
							{text('connect.again.open')}
						</Button>
					}
				/>
			) : null}
			{failure?.error && !failure.blocked ? <Problem error={failure.error} title={failure.words} tone={failure.words ? 'warning' : undefined} /> : null}
			{failure && !failure.error && !failure.blocked ? <Callout tone="warning" live title={failure.words ?? ''} /> : null}
		</>
	);
}
